import { useNavigate } from 'react-router-dom';
import { expression } from '~/components/ChatAvatar/Canvas/Character/animationData/expression';
import {
  useLeaveChatRoomMutation,
  useLeaveGroupChatRoomMutation,
} from '~/features/chatRoom';
import { LeaveChatRoomRequest } from '~/types';
import { PAGE_ROUTE, SECOND } from '~/constants';
import { AnimationDuration } from '~/constants/Canvas';
import { useChatAvatarStore, useCommonChatStore } from '~/store';

interface UseCommonChatStateProps {
  chatType: 'group' | 'personal' | 'mission';
  chatRoomId?: string;
  email: string;
  setWaitingChat: React.Dispatch<React.SetStateAction<boolean>>;
}

const useCommonChatState = ({
  chatType,
  chatRoomId,
  email,
  setWaitingChat,
}: UseCommonChatStateProps) => {
  const navigate = useNavigate();

  const showCharacter = useCommonChatStore((state) => state.showCharacter);
  const avatarInfo = useCommonChatStore((state) => state.avatarInfo);
  const backgroundInfo = useCommonChatStore((state) => state.background_url);
  const safe_chat = useCommonChatStore((state) => state.isSafeChatOn);

  const { showLeaveRoomOpen, setShowLeaveRoomOpen } = useCommonChatStore(
    (state) => ({
      showLeaveRoomOpen: state.showLeaveRoomOpen,
      setShowLeaveRoomOpen: state.setShowLeaveRoomOpen,
    }),
  );
  const { isChatLayoutShow, setIsChatLayoutShow } = useCommonChatStore(
    (state) => ({
      isChatLayoutShow: state.isChatLayoutShow,
      setIsChatLayoutShow: state.setIsChatLayoutShow,
    }),
  );

  const isTextFocus = useCommonChatStore((state) => state.isTextFocus);

  const setAvatarQueue = useChatAvatarStore((state) => state.setAvatarQueue);
  const setAnimationState = useChatAvatarStore(
    (state) => state.setAnimationState,
  );

  const {
    mutate: leavePersonalChatRoomMutate,
    isPending: isPersonalLeavePending,
  } = useLeaveChatRoomMutation();
  const { mutate: leaveGroupChatRoomMutate, isPending: isGroupLeavePending } =
    useLeaveGroupChatRoomMutation();

  const handleChatLayoutClose = () => {
    setIsChatLayoutShow(!isChatLayoutShow);
  };

  const handleRoomLeaveClick = async () => {
    if (!chatRoomId) return;

    const requestData: LeaveChatRoomRequest = {
      chatroom_id: chatRoomId,
    };

    if (chatType === 'group') {
      leaveGroupChatRoomMutate(requestData, {
        onSuccess: () => {
          setShowLeaveRoomOpen(false);
          navigate(PAGE_ROUTE.CHAT_LIST, { replace: true });
        },
      });
      return;
    }
    if (chatType === 'personal') {
      leavePersonalChatRoomMutate(requestData, {
        onSuccess: () => {
          setShowLeaveRoomOpen(false);
          navigate(PAGE_ROUTE.CHAT_LIST, { replace: true });
        },
      });
      return;
    }
  };

  const handleSetWaitingChatTrue = async () => {
    try {
      if (avatarInfo && avatarInfo.length > 0) {
        avatarInfo.forEach((el) => {
          setAvatarQueue({
            id: el.id,
            queue: {
              type: 'Neutral',
              name: 'neutral01',
              duration: AnimationDuration.texting,
              value: expression['neutral'].morphTargets,
            },
          });
          setAnimationState({ id: el.id, state: { type: 'Texting' } });
        });

        setWaitingChat(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const isLeaveRoomPending = isGroupLeavePending || isPersonalLeavePending;

  return {
    safe_chat,
    showCharacter,
    avatarInfo,
    backgroundInfo,
    showLeaveRoomOpen,
    setShowLeaveRoomOpen,
    isTextFocus,
    setAvatarQueue,
    handleChatLayoutClose,
    isChatLayoutShow,
    setIsChatLayoutShow,
    handleRoomLeaveClick,
    isLeaveRoomPending,
    handleSetWaitingChatTrue,
  };
};

export default useCommonChatState;
